import { useFeatureAccess } from '@/hooks/use-feature-access.hook';

/**
 * Who gets the agents surface: the agents index, plays, knowledge, the agent
 * workspace and the full skills library. `unlocked` is true once the
 * entitlements say the organization is not AppSumo-only (an AppSumo
 * redemption with no paid plan; a stacked Sumoling who pays passes) and its
 * plan allows building agents. Anyone else opens the Agents page on its
 * Skills tab (Research, Qualification and their custom record skills) and
 * gets the upgrade card everywhere else, as the server's
 * `require_gtm_agents` fence already decides.
 *
 * Fails closed: while the entitlements load, for no active organization, or
 * after a failed first read, the surface stays locked. A failed background
 * refetch keeps the last verdict, so a paid organization's open workspace is
 * not torn down by a network blip.
 */
export function useAgentsAccess() {
  const {
    isReady: entitlementsReady,
    organizationId,
    isAppSumoOnly,
    error,
    actions,
  } = useFeatureAccess();
  // A verdict on the AppSumo fence for this organization.
  const fenceSettled = entitlementsReady && Boolean(organizationId);
  // Still waiting on the entitlements; an error counts as an answer (locked).
  const fencePending = !entitlementsReady && !error;
  return {
    unlocked: fenceSettled && !isAppSumoOnly && (actions?.agents_build ?? true),
    // The agents redesign's chat look (useGumloopChat) follows the AppSumo
    // fence only: a Free org that can't build agents still gets the new chat,
    // onboarding's first chat included. What it may do stays with `unlocked`
    // and the server.
    chatLookUnlocked: fenceSettled && !isAppSumoOnly,
    isReady: !fencePending,
    fencePending,
  };
}
